import React from 'react';
import swal from 'sweetalert';
import Swal from 'sweetalert2';
import { updateJobStatus } from '../Services/jobService';
import { hasExpired } from '../Services/utils';
import * as HelpMessages from '../Services/helpMessages';
import { JobStatus } from './enums';

export const closeJob = (job, t, onStatusRefresh, toggleConfigureEmailModal, newJobStatus = JobStatus.Closed) => {
  Swal.fire({
    style: { zIndex: 100 },
    title: newJobStatus === JobStatus.Closed ? t('jobs.CLOSE_SELECTED_JOB') : t('jobs.DELETE_JOB_TITLE'),
    icon: 'warning',
    html: `
        ${t('jobs.DELETE_JOB_TEXT')}
        <p></p>
        <h4>
          <input type="checkbox" id="changeAllApplicationsToDeclined">
          ${t('jobs.APPLICATIONS_TO_DECLINED')}
        </h4>
        <p></p>
        <h4>
          <input type="checkbox" id="informApplicantsAboutDeletion">
          ${t('jobs.INFORM_APPLICANTS_ABOUT_JOB_CLOSURE')}
          <small><a id="testEmailLink" style="cursor: pointer; color: #007bff;">${t(
            'email.CONFIGURE_EMAIL'
          )}</a></small>
        </h4>
      `,
    showCancelButton: true,
    cancelButtonText: t('common.CANCEL'),
    preConfirm: () => {
      // pre-confirm has to be added if we want to add additional properties.
      var changeAllApplicationsToDeclined = Swal.getPopup().querySelector('#changeAllApplicationsToDeclined').checked;
      var informApplicantsAboutDeletion = Swal.getPopup().querySelector('#informApplicantsAboutDeletion').checked;
      return { informApplicantsAboutDeletion, changeAllApplicationsToDeclined };
    },
    didOpen: () => {
      document.getElementById('testEmailLink').addEventListener('click', () => {
        toggleConfigureEmailModal();
        Swal.close();
      });
    },
  }).then((swalResult) => {
    if (swalResult.isConfirmed) {
      const { informApplicantsAboutDeletion, changeAllApplicationsToDeclined } = swalResult.value;

      updateJobStatus(job.id, newJobStatus, true, changeAllApplicationsToDeclined, informApplicantsAboutDeletion).then(
        (response) => {
          onStatusRefresh(newJobStatus);
        }
      );
    }
  });
};

export const publishJob = (job, t, onStatusRefresh) => {
  if (hasExpired(job.expirationDate)) {
    // There are 4 predefined ICONS: "warning", "error", "success" and "info".
    swal({
      title: t('jobs.CAN_NOT_PUBLISH'),
      text: t('jobs.CAN_NOT_PUBLISH_INSTRUCTION'),
      icon: 'error',
      buttons: {
        confirm: {
          text: t('common.OK'),
          value: true,
          visible: true,
          closeModal: true,
        },
      },
      dangerMode: false,
    });
    return;
  }

  swal({
    title: t('jobs.PUBLISH_SELECTED_JOB'),
    text: t('jobs.PUBLISH_SELECTED_JOB_INFO'),
    icon: 'warning',
    buttons: {
      cancel: {
        text: t('common.CANCEL'),
        visible: true,
        closeModal: true,
      },
      confirm: {
        text: t('jobs.PUBLISH'),
        value: true,
        visible: true,
        closeModal: true,
      },
    },
  }).then((willPublish) => {
    if (willPublish) {
      // Check at backend if Job is suitable for publishing,
      // If yes -> publish immediately on backend,
      // if no -> write out warning
      updateJobStatus(job.id, JobStatus.Published)
        .then((response) => {
          if (response.data === '' && response.status === 200) {
            onStatusRefresh(JobStatus.Published);
          } else {
            swal({
              title: t('common.CONFIRMATION_NEEDED'),
              text: t('jobs.NO_COMPETENCES_WARNING'),
              icon: 'warning',
              buttons: {
                cancel: {
                  text: t('common.CANCEL'),
                  visible: true,
                  closeModal: true,
                },
                confirm: {
                  text: t('jobs.PUBLISH_ANYWAY'),
                  value: true,
                  visible: true,
                  closeModal: true,
                },
              },
              dangerMode: false,
            }).then((doPublish) => {
              if (doPublish) {
                job.jobStatus = JobStatus.Published;
                updateJobStatus(job.id, JobStatus.Published, true).then((response) => {
                  onStatusRefresh(JobStatus.Published);
                });
              }
            });
          }
        })
        .catch((error) => {
          console.error({ error });
        });
    }
  });
};

export const archiveJob = (job, t, onStatusRefresh) => {
  swal({
    title: t('jobs.ARCHIVE_SELECTED_JOB'),
    text: t('jobs.ARCHIVE_SELECTED_JOB_INFO'),
    icon: 'warning',
    buttons: {
      cancel: {
        text: t('common.CANCEL'),
        visible: true,
        closeModal: true,
      },
      confirm: {
        text: t('jobs.ARCHIVE'),
        value: true,
        visible: true,
        closeModal: true,
      },
    },
    dangerMode: true,
  }).then((willArchive) => {
    if (willArchive) {
      job.jobStatus = JobStatus.Archived;
      updateJobStatus(job.id, job.jobStatus).then((response) => {
        onStatusRefresh(JobStatus.Archived);
      });
    }
  });
};

export const getJobStatusInfo = (jobStatus, language) => {
  if (jobStatus === JobStatus.Published) {
    return HelpMessages.getPublishedJobStatusText(language);
  } else if (jobStatus === JobStatus.Draft) {
    return HelpMessages.getDraftJobStatusText(language);
  } else if (jobStatus === JobStatus.Archived) {
    return HelpMessages.getArchivedJobStatusText(language);
  } else if (jobStatus === JobStatus.Closed) {
    return HelpMessages.getClosedJobStatusText(language);
  } else if (jobStatus === JobStatus.Expired) {
    return HelpMessages.getExpiredJobStatusText(language);
  }

  return jobStatus;
};

export const displayPaginationMessage = (pagination, t) => {
  if (!pagination) {
    return;
  }

  const from = (pagination.currentPage - 1) * pagination.itemsPerPage + 1;
  let to = pagination.currentPage * pagination.itemsPerPage;

  if (to > pagination.totalItems) {
    to = pagination.totalItems;
  }

  return (
    <div className="text-right">
      <b>{from}</b> {t('jobs.TO')} <b>{to}</b> {t('jobs.OF')} <b>{pagination.totalItems}</b>
    </div>
  );
};
